import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation } from '@tanstack/react-query';
import { format, startOfWeek, endOfWeek, addDays } from 'date-fns';
import {
  Calendar,
  Clock,
  DollarSign,
  Save,
  AlertCircle,
  CheckCircle,
  Users,
  Calculator,
  FileText,
  Download,
  Upload,
  RefreshCw,
  TrendingUp,
  Info
} from 'lucide-react';
import { managerService } from '../../services/managerService';
import { useWebSocket } from '../../contexts/WebSocketContext';
import toast from 'react-hot-toast';

interface Employee {
  id: string;
  name: string;
  role: string;
  hourlyRate: number;
  defaultHours: number;
  department: string;
}

interface PayrollEntry {
  employeeId: string;
  regularHours: number;
  overtimeHours: number;
  tips: number;
  deductions: number;
  notes: string;
}

interface PayrollFormData {
  weekStartDate: string;
  entries: PayrollEntry[];
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalGrossPay: number;
  status: 'draft' | 'submitted' | 'approved';
}

const PayrollEntryComponent: React.FC = () => {
  const { socket } = useWebSocket();
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [showSummary, setShowSummary] = useState(false);
  const [aiSuggestions, setAISuggestions] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const weekStart = startOfWeek(selectedWeek);
  const weekEnd = endOfWeek(selectedWeek);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<PayrollFormData>();

  // Fetch employees
  const { data: employees, isLoading: loadingEmployees } = useQuery({
    queryKey: ['employees'],
    queryFn: managerService.getEmployees
  });

  // Fetch existing payroll data for the week
  const { data: existingPayroll, refetch: refetchPayroll } = useQuery({
    queryKey: ['payroll', format(weekStart, 'yyyy-MM-dd')],
    queryFn: () => managerService.getPayrollForWeek(format(weekStart, 'yyyy-MM-dd')),
    enabled: !!weekStart
  });

  // Save payroll mutation
  const savePayrollMutation = useMutation({
    mutationFn: (data: PayrollFormData) => managerService.savePayroll(data),
    onSuccess: () => {
      toast.success('Payroll saved successfully!');
      refetchPayroll();
    },
    onError: () => {
      toast.error('Failed to save payroll');
    }
  });

  // Submit payroll mutation
  const submitPayrollMutation = useMutation({
    mutationFn: (data: PayrollFormData) => managerService.submitPayroll(data),
    onSuccess: () => {
      toast.success('Payroll submitted successfully!');
      refetchPayroll();
    },
    onError: () => {
      toast.error('Failed to submit payroll');
    }
  });

  // WebSocket for AI suggestions
  useEffect(() => {
    if (socket) {
      socket.on('payroll:suggestion', (suggestion) => {
        setAISuggestions(prev => [...prev, suggestion]);
      });

      return () => {
        socket.off('payroll:suggestion');
      };
    }
  }, [socket]);

  // Calculate totals
  const watchedEntries = watch('entries') || [];
  const calculateTotals = () => {
    let totalRegular = 0;
    let totalOvertime = 0;
    let totalGross = 0;

    watchedEntries.forEach((entry: any, index: number) => {
      const employee = employees?.find((e: Employee) => e.id === entry.employeeId);
      if (employee) {
        const regularPay = entry.regularHours * employee.hourlyRate;
        const overtimePay = entry.overtimeHours * employee.hourlyRate * 1.5;
        const gross = regularPay + overtimePay + (entry.tips || 0) - (entry.deductions || 0);
        
        totalRegular += entry.regularHours || 0;
        totalOvertime += entry.overtimeHours || 0;
        totalGross += gross;
      }
    });

    setValue('totalRegularHours', totalRegular);
    setValue('totalOvertimeHours', totalOvertime);
    setValue('totalGrossPay', totalGross);
  };

  useEffect(() => {
    calculateTotals();
  }, [watchedEntries]);

  const onSubmit = async (data: PayrollFormData, action: 'save' | 'submit') => {
    data.weekStartDate = format(weekStart, 'yyyy-MM-dd');
    data.status = action === 'save' ? 'draft' : 'submitted';

    if (action === 'save') {
      await savePayrollMutation.mutateAsync(data);
    } else {
      await submitPayrollMutation.mutateAsync(data);
    }
  };

  const handleAIAssist = async () => {
    setIsProcessing(true);
    try {
      const result = await managerService.getPayrollAISuggestions({
        weekStart: format(weekStart, 'yyyy-MM-dd'),
        employees: employees || []
      });
      setAISuggestions(result.suggestions || []);
      
      // Apply suggestions to form
      if (result.entries) {
        result.entries.forEach((entry: any, index: number) => {
          setValue(`entries.${index}.regularHours`, entry.regularHours);
          setValue(`entries.${index}.overtimeHours`, entry.overtimeHours);
        });
      }
    } catch (error) {
      toast.error('Failed to get AI suggestions');
    } finally {
      setIsProcessing(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 }
    }
  };

  if (loadingEmployees) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Weekly Payroll Entry</h1>
          <p className="text-gray-600 mt-2">
            Week of {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setSelectedWeek(addDays(selectedWeek, -7))}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            ←
          </button>
          <button
            onClick={() => setSelectedWeek(new Date())}
            className="px-4 py-2 text-sm font-medium bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Current Week
          </button>
          <button
            onClick={() => setSelectedWeek(addDays(selectedWeek, 7))}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            →
          </button>
        </div>
      </motion.div>

      {/* AI Assistant Banner */}
      <motion.div variants={itemVariants} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Calculator className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">AI Payroll Assistant</h3>
              <p className="text-sm text-gray-600">
                Let AI analyze time cards and suggest payroll entries
              </p>
            </div>
          </div>
          <button
            onClick={handleAIAssist}
            disabled={isProcessing}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <TrendingUp className="h-4 w-4 mr-2" />
                Get AI Suggestions
              </>
            )}
          </button>
        </div>

        {/* AI Suggestions */}
        {aiSuggestions.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-medium text-gray-700">AI Insights:</h4>
            {aiSuggestions.map((suggestion, index) => (
              <div key={index} className="flex items-start space-x-2 text-sm">
                <Info className="h-4 w-4 text-blue-500 mt-0.5" />
                <span className="text-gray-700">{suggestion.message}</span>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Payroll Form */}
      <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit((data) => onSubmit(data, 'save'))}>
          <div className="space-y-6">
            {/* Employee Entries */}
            <div>
              <h2 className="text-lg font-semibold mb-4">Employee Hours</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Employee</th>
                      <th className="text-left py-3 px-4">Role</th>
                      <th className="text-center py-3 px-4">Regular Hours</th>
                      <th className="text-center py-3 px-4">Overtime Hours</th>
                      <th className="text-center py-3 px-4">Tips</th>
                      <th className="text-center py-3 px-4">Deductions</th>
                      <th className="text-center py-3 px-4">Gross Pay</th>
                      <th className="text-center py-3 px-4">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees?.map((employee: Employee, index: number) => {
                      const regularHours = watch(`entries.${index}.regularHours`) || 0;
                      const overtimeHours = watch(`entries.${index}.overtimeHours`) || 0;
                      const tips = watch(`entries.${index}.tips`) || 0;
                      const deductions = watch(`entries.${index}.deductions`) || 0;
                      
                      const regularPay = regularHours * employee.hourlyRate;
                      const overtimePay = overtimeHours * employee.hourlyRate * 1.5;
                      const grossPay = regularPay + overtimePay + tips - deductions;

                      return (
                        <tr key={employee.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{employee.name}</p>
                              <p className="text-xs text-gray-500">${employee.hourlyRate}/hr</p>
                            </div>
                            <input
                              type="hidden"
                              {...register(`entries.${index}.employeeId`)}
                              value={employee.id}
                            />
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{employee.role}</td>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              step="0.5"
                              className="w-20 text-center border rounded px-2 py-1"
                              {...register(`entries.${index}.regularHours`, {
                                min: 0,
                                max: 60
                              })}
                              defaultValue={employee.defaultHours}
                            />
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              step="0.5"
                              className="w-20 text-center border rounded px-2 py-1"
                              {...register(`entries.${index}.overtimeHours`, {
                                min: 0,
                                max: 40
                              })}
                              defaultValue={0}
                            />
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              step="0.01"
                              className="w-20 text-center border rounded px-2 py-1"
                              {...register(`entries.${index}.tips`, { min: 0 })}
                              defaultValue={0}
                            />
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              step="0.01"
                              className="w-20 text-center border rounded px-2 py-1"
                              {...register(`entries.${index}.deductions`, { min: 0 })}
                              defaultValue={0}
                            />
                          </td>
                          <td className="py-3 px-4 text-center font-medium">
                            ${grossPay.toFixed(2)}
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="text"
                              className="w-32 border rounded px-2 py-1 text-sm"
                              {...register(`entries.${index}.notes`)}
                              placeholder="Notes..."
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={2} className="py-3 px-4 font-semibold">Totals</td>
                      <td className="py-3 px-4 text-center font-semibold">
                        {watch('totalRegularHours') || 0}
                      </td>
                      <td className="py-3 px-4 text-center font-semibold">
                        {watch('totalOvertimeHours') || 0}
                      </td>
                      <td className="py-3 px-4"></td>
                      <td className="py-3 px-4"></td>
                      <td className="py-3 px-4 text-center font-semibold text-green-600">
                        ${(watch('totalGrossPay') || 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-6 border-t">
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setShowSummary(!showSummary)}
                  className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {showSummary ? 'Hide' : 'Show'} Summary
                </button>
                <button
                  type="button"
                  className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </button>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  type="submit"
                  className="flex items-center px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Draft
                </button>
                <button
                  type="button"
                  onClick={() => handleSubmit((data) => onSubmit(data, 'submit'))()}
                  className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Submit Payroll
                </button>
              </div>
            </div>
          </div>
        </form>
      </motion.div>

      {/* Payroll Summary */}
      {showSummary && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h2 className="text-lg font-semibold mb-4">Payroll Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Hours Breakdown</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Regular Hours:</span>
                  <span className="font-medium">{watch('totalRegularHours') || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Overtime Hours:</span>
                  <span className="font-medium">{watch('totalOvertimeHours') || 0}</span>
                </div>
                <div className="flex justify-between font-semibold pt-2 border-t">
                  <span>Total Hours:</span>
                  <span>{(watch('totalRegularHours') || 0) + (watch('totalOvertimeHours') || 0)}</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Cost Analysis</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Labor Cost:</span>
                  <span className="font-medium">${(watch('totalGrossPay') || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Average per Employee:</span>
                  <span className="font-medium">
                    ${((watch('totalGrossPay') || 0) / (employees?.length || 1)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Status</h3>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  {existingPayroll?.status === 'approved' ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-green-700 font-medium">Approved</span>
                    </>
                  ) : existingPayroll?.status === 'submitted' ? (
                    <>
                      <Clock className="h-5 w-5 text-yellow-500" />
                      <span className="text-yellow-700 font-medium">Pending Approval</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-5 w-5 text-gray-400" />
                      <span className="text-gray-600">Draft</span>
                    </>
                  )}
                </div>
                {existingPayroll?.approvedBy && (
                  <p className="text-sm text-gray-600">
                    Approved by: {existingPayroll.approvedBy}
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PayrollEntryComponent;